import { Button } from "@/components/ui/Button";
import { Logo } from "@/components/ui/Logo";
import { Users, Youtube, Instagram, MoreVertical, Plus } from "lucide-react";
import { useState, useEffect, useRef } from "react";

import { useTranslation } from 'react-i18next';
interface ContentCard {
    id: string;
    column: string;
    title: string;
    platform: "instagram" | "youtube" | "tiktok";
}

export const AnimatedKanbanHero = () => {
    const { t } = useTranslation();
    const [cards, setCards] = useState<ContentCard[]>([
        { id: "1", column: "pending", title: "Product Launch Post", platform: "instagram" },
        { id: "2", column: "pending", title: "Behind the Scenes", platform: "instagram" },
        { id: "3", column: "process", title: "Tutorial Video", platform: "youtube" },
        { id: "4", column: "idea", title: "5 MacBook Silicon Hacks", platform: "instagram" },
        { id: "5", column: "publish", title: "Weekly Tips", platform: "instagram" },
        { id: "6", column: "publish", title: "Q&A Session", platform: "tiktok" },
    ]);

    const [draggedCard, setDraggedCard] = useState<string | null>(null);
    const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
    const [floatingCard, setFloatingCard] = useState<ContentCard | null>(null);
    const [floatingPosition, setFloatingPosition] = useState({ x: 0, y: 0 });
    const [isAnimating, setIsAnimating] = useState(false);
    const columnRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const boardRef = useRef<HTMLDivElement>(null);

    const columns = [
        { id: "idea", title: "Idea" },
        { id: "process", title: "Process" },
        { id: "pending", title: "Pending" },
        { id: "publish", title: "Publish" },
    ];

    const animationSequence = [
        { cardId: "4", from: "idea", to: "process" },
        { cardId: "3", from: "process", to: "pending" },
        { cardId: "1", from: "pending", to: "publish" },
        { cardId: "2", from: "pending", to: "process" },
    ];

    useEffect(() => {
        let sequenceIndex = 0;
        let animationTimer: ReturnType<typeof setTimeout>;

        const runAnimation = () => {
            if (sequenceIndex >= animationSequence.length) {
                sequenceIndex = 0;
            }

            const currentAnimation = animationSequence[sequenceIndex];
            if (!currentAnimation) {
                sequenceIndex++;
                animationTimer = setTimeout(runAnimation, 1000);
                return;
            }

            const { cardId, from, to } = currentAnimation;
            const card = cards.find(c => c.id === cardId && c.column === from);

            if (!card || !columnRefs.current[from] || !columnRefs.current[to] || !boardRef.current) {
                sequenceIndex++;
                animationTimer = setTimeout(runAnimation, 1000);
                return;
            }

            const boardRect = boardRef.current.getBoundingClientRect();
            const fromRect = columnRefs.current[from]!.getBoundingClientRect();
            const toRect = columnRefs.current[to]!.getBoundingClientRect();

            // Start position (center of from column)
            const startX = fromRect.left - boardRect.left + fromRect.width / 2 - 90;
            const startY = fromRect.top - boardRect.top + 80;

            // End position (center of to column)
            const endX = toRect.left - boardRect.left + toRect.width / 2 - 90;
            const endY = toRect.top - boardRect.top + 80;

            setIsAnimating(true);
            setFloatingCard(card);
            setFloatingPosition({ x: startX, y: startY });
            setDragOverColumn(null);

            // Hide original card
            setCards(prev => prev.map(c =>
                c.id === cardId ? { ...c, column: "_hidden" } : c
            ));

            // Animate to destination
            setTimeout(() => {
                setFloatingPosition({ x: endX, y: endY });
                setDragOverColumn(to);
            }, 50);

            // Complete animation
            setTimeout(() => {
                setCards(prev => prev.map(c =>
                    c.id === cardId ? { ...c, column: to } : c
                ));
                setFloatingCard(null);
                setDragOverColumn(null);
                setIsAnimating(false);
                sequenceIndex++;
                animationTimer = setTimeout(runAnimation, 1200);
            }, 600);
        };

        const initialTimer = setTimeout(runAnimation, 1500);

        return () => {
            clearTimeout(initialTimer);
            clearTimeout(animationTimer);
        };
    }, []);

    const handleDragStart = (cardId: string) => {
        if (isAnimating) return;
        setDraggedCard(cardId);
    };

    const handleDragOver = (columnId: string) => {
        if (isAnimating) return;
        setDragOverColumn(columnId);
    };

    const handleDrop = (targetColumn: string) => {
        if (draggedCard && !isAnimating) {
            setCards(prev => prev.map(card =>
                card.id === draggedCard ? { ...card, column: targetColumn } : card
            ));
        }
        setDraggedCard(null);
        setDragOverColumn(null);
    };

    const handleDragEnd = () => {
        setDraggedCard(null);
        setDragOverColumn(null);
    };

    const getCardsForColumn = (columnId: string) => cards.filter(c => c.column === columnId);

    const getPlatformBadge = (platform: string) => {
        const config = {
            instagram: { bg: "bg-pink-100 dark:bg-pink-900/30", text: "text-pink-600 dark:text-pink-400", icon: <Instagram className="w-3 h-3" /> },
            youtube: { bg: "bg-red-100 dark:bg-red-900/30", text: "text-red-600 dark:text-red-400", icon: <Youtube className="w-3 h-3" /> },
            tiktok: {
                bg: "bg-gray-900 dark:bg-gray-700", text: "text-white", icon: (
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                    </svg>
                )
            }
        };
        const { bg, text, icon } = config[platform as keyof typeof config] || config.instagram;

        return (
            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${bg} ${text} text-xs font-medium`}>
                {icon}
                <span className="capitalize">{platform}</span>
            </div>
        );
    };

    const renderCard = (card: ContentCard, isFloating = false) => (
        <div
            className={`
        bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700
        ${isFloating ? "shadow-2xl scale-105 rotate-3" : "cursor-grab active:cursor-grabbing hover:scale-[1.02]"}
        transition-all duration-200
      `}
            style={{ width: isFloating ? "180px" : "auto" }}
        >
            <div className="flex items-start gap-2">
                <div className="flex flex-col gap-0.5 text-gray-300 dark:text-gray-600">
                    {[0, 1, 2].map(i => (
                        <div key={i} className="flex gap-0.5">
                            <div className="w-1 h-1 rounded-full bg-current"></div>
                            <div className="w-1 h-1 rounded-full bg-current"></div>
                        </div>
                    ))}
                </div>
                <div className="flex-1 min-w-0">
                    {getPlatformBadge(card.platform)}
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mt-2 truncate">{card.title}</p>
                    <div className="flex items-center gap-2 mt-2">
                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-pink-400 to-purple-400"></div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="w-full h-full rounded-2xl overflow-hidden">
            <div className="relative border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex flex-col w-[115%] h-[115%] transform scale-[0.87] origin-top-left rounded-[20px]">
                {/* Top Navigation */}
                <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-4 py-3 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-6">
                        <Logo />
                        <nav className="hidden md:flex items-center gap-1">
                            {["Dashboard", "Boards", "Analytics", "Settings"].map((item, i) => (
                                <button
                                    key={item}
                                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${i === 1 ? "bg-primary-600/10 text-primary-600 dark:text-primary-400" : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                                        }`}
                                >
                                    {item}
                                </button>
                            ))}
                        </nav>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="hidden sm:flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                            <Users className="w-4 h-4" />
                            <span>Members (3)</span>
                        </div>
                        <Button size="sm" className="bg-primary-600 hover:bg-primary-700 text-white">
                            + Invite
                        </Button>
                    </div>
                </div>

                <div className="flex flex-1 min-h-0">
                    {/* Sidebar */}
                    <div className="w-48 bg-gray-50 dark:bg-gray-800/50 border-r border-gray-200 dark:border-gray-700 p-4 flex-shrink-0 overflow-y-auto">
                        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">{t('landing.mock.channels')}</div>
                        <div className="space-y-1">
                            {[
                                { name: "YouTube", sub: "planwise.social", icon: <Youtube className="w-4 h-4 text-white" />, bg: "bg-red-500" },
                                { name: "Instagram", sub: "planwise.social", icon: <Instagram className="w-4 h-4 text-white" />, bg: "bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400", active: true },
                                { name: "TikTok", sub: "planwise.social", icon: <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" /></svg>, bg: "bg-black dark:bg-gray-900" },
                            ].map((channel) => (
                                <div key={channel.name} className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${channel.active ? "bg-white dark:bg-gray-700 shadow-sm" : "hover:bg-white dark:hover:bg-gray-700"}`}>
                                    <div className={`w-8 h-8 rounded-full ${channel.bg} flex items-center justify-center`}>
                                        {channel.icon}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{channel.name}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{channel.sub}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Main Board Content */}
                    <div className="flex flex-col flex-1 p-4 bg-gray-50 dark:bg-gray-900/50 overflow-hidden relative" ref={boardRef}>
                        {/* Board Header */}
                        <div className="flex items-center gap-3 mb-4 flex-shrink-0">
                            <button className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 text-sm">← Back</button>
                            <h2 className="text-lg font-bold text-gray-800 dark:text-gray-200">{t('landing.mock.contentBoard')}</h2>
                        </div>

                        {/* Floating Animated Card */}
                        {floatingCard && (
                            <div
                                className="absolute z-50 pointer-events-none"
                                style={{
                                    transform: `translate(${floatingPosition.x}px, ${floatingPosition.y}px)`,
                                    transition: "transform 0.55s cubic-bezier(0.4, 0, 0.2, 1)",
                                }}
                            >
                                {renderCard(floatingCard, true)}
                            </div>
                        )}

                        {/* Kanban Columns */}
                        <div className="flex gap-3 flex-1 min-h-0 overflow-x-auto">
                            {columns.map((column) => {
                                const columnCards = getCardsForColumn(column.id);
                                const isDragOver = dragOverColumn === column.id;

                                return (
                                    <div
                                        key={column.id}
                                        ref={(el) => { columnRefs.current[column.id] = el; }}
                                        className={`w-[200px] flex-shrink-0 flex flex-col transition-all duration-300 ${isDragOver ? "scale-[1.02]" : ""
                                            }`}
                                        onDragOver={(e) => {
                                            e.preventDefault();
                                            handleDragOver(column.id);
                                        }}
                                        onDragLeave={() => setDragOverColumn(null)}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            handleDrop(column.id);
                                        }}
                                    >
                                        {/* Column Header */}
                                        <div className="flex items-center justify-between mb-3 px-1 flex-shrink-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold text-gray-800 dark:text-gray-200 text-sm">{column.title}</span>
                                                <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                                                    {columnCards.length}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <button className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-gray-500 dark:text-gray-400">
                                                    <Plus className="w-4 h-4" />
                                                </button>
                                                <button className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-gray-500 dark:text-gray-400">
                                                    <MoreVertical className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Column Content */}
                                        <div
                                            className={`
                        bg-gray-100 dark:bg-gray-800 rounded-xl p-2 flex-1 overflow-y-auto transition-all duration-500
                        ${isDragOver ? "bg-primary-600/10 ring-2 ring-primary-600/30 scale-[1.01]" : ""}
                      `}
                                        >
                                            {columnCards.length === 0 ? (
                                                <div className="flex flex-col items-center justify-center h-32 text-gray-400 dark:text-gray-500 text-xs text-center">
                                                    <p>{t('landing.mock.dropCardsHere')}</p>
                                                    <p>click + to add</p>
                                                </div>
                                            ) : (
                                                <div className="space-y-2">
                                                    {columnCards.map((card) => (
                                                        <div
                                                            key={card.id}
                                                            draggable={!isAnimating}
                                                            onDragStart={() => handleDragStart(card.id)}
                                                            onDragEnd={handleDragEnd}
                                                            className={`
                                transition-all duration-300
                                ${draggedCard === card.id ? "opacity-50 scale-95" : ""}
                              `}
                                                        >
                                                            {renderCard(card)}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>


        </div>
    );
};
